import { useSession } from "@pontimed/auth-client";
import { Navigate, createFileRoute, useRouterState } from "@tanstack/react-router";

import { Skeleton } from "@/components/ui/skeleton";

/**
 * Root index — auth-aware redirect:
 *   loading       → skeleton
 *   authenticated → /account/user/dashboard
 *   guest         → /find  (public search; D2 in _docs/web-portal-navigation.md)
 *
 * `?legal=<segment>` (set by the `/legal/*` redirects) is forwarded on both
 * hops so `<LegalSheetProvider>` opens the document on the landing page.
 */
export const Route = createFileRoute("/")({
  component: IndexRoute,
});

function IndexRoute() {
  const { status } = useSession();
  const legal = useRouterState({
    select: (s) => (s.location.search as Record<string, unknown>).legal,
  });
  // The router's search schema has no `legal` key (it is read raw by the
  // legal sheet) — cast through `never`, same as legal-sheet-provider.tsx.
  const search = typeof legal === "string" ? ({ legal } as never) : undefined;

  if (status === "loading") {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Skeleton className="h-12 w-48" />
      </div>
    );
  }

  if (status === "authenticated") {
    return <Navigate to="/account/user/dashboard" search={search} replace />;
  }

  return <Navigate to="/find" search={search} replace />;
}
